import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

interface Props {
  titel: ReactNode;
  ondertitel?: ReactNode | undefined;
  acties?: ReactNode | undefined;
  terug?: { naar: string; label: string } | undefined;
  children: ReactNode;
}

/**
 * De kop van een pagina: titel, eventueel een ondertitel, een link terug en de belangrijkste acties.
 * @param props De titel, ondertitel, acties, link terug en inhoud.
 * @returns De pagina.
 */
export function Pagina({ titel, ondertitel, acties, terug, children }: Props) {
  return (
    <Box>
      {terug && (
        <Button
          component={Link}
          to={terug.naar}
          nativeButton={false}
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ mb: 1, ml: -1 }}
        >
          {terug.label}
        </Button>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { md: 'flex-start' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h1" sx={{ overflowWrap: 'anywhere' }}>
            {titel}
          </Typography>
          {ondertitel && (
            <Typography color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {ondertitel}
            </Typography>
          )}
        </Box>
        {acties && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', flexShrink: 0 }}>
            {acties}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

/**
 * Een kop van een onderdeel binnen een pagina, met eventueel acties rechts.
 * @param props De titel en acties.
 * @returns De kop.
 */
export function Onderdeelkop({ titel, acties }: { titel: ReactNode; acties?: ReactNode | undefined }) {
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Typography variant="h6" component="h2">
        {titel}
      </Typography>
      {acties && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {acties}
        </Stack>
      )}
    </Stack>
  );
}
